import { DateTime } from 'luxon';
import { isOpenAllDay } from '@/features/userManagement/weeklyCallSchedule';
import type { WeeklyCallDay, WeeklyCallSchedule, WeeklyCallSlot } from '@/types/api';

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function viewerDiffersFromEstablishment(
  establishmentTimezone: string,
  viewerTimezone: string = browserTimeZone()
): boolean {
  return establishmentTimezone !== viewerTimezone;
}

export function convertWallClockToZone(
  clock: string,
  dayOfWeek: number,
  fromZone: string,
  toZone: string
): string {
  const [hour, minute] = clock.split(':').map(Number);
  const mondayAnchor = DateTime.fromObject(
    { year: 2000, month: 1, day: 3 },
    { zone: fromZone }
  );
  return mondayAnchor
    .plus({ days: dayOfWeek })
    .set({ hour, minute, second: 0, millisecond: 0 })
    .setZone(toZone)
    .toFormat('HH:mm');
}

function formatWindowWithViewerLocal(
  window: WeeklyCallSlot,
  dayOfWeek: number,
  schedule: WeeklyCallSchedule,
  viewerTimezone: string
): string {
  const base = `${window.start}–${window.end}`;
  const startLocal = convertWallClockToZone(
    window.start,
    dayOfWeek,
    schedule.timezone,
    viewerTimezone
  );
  const endLocal = convertWallClockToZone(
    window.end,
    dayOfWeek,
    schedule.timezone,
    viewerTimezone
  );
  return `${base} (${startLocal}–${endLocal})`;
}

export function daySummaryWithViewerLocal(
  day: WeeklyCallDay,
  schedule: WeeklyCallSchedule,
  openLabel: string,
  closedLabel: string,
  viewerTimezone: string | null
): string {
  if (day.closed) {
    return closedLabel;
  }
  if (isOpenAllDay(day)) {
    return openLabel;
  }
  const showViewer =
    viewerTimezone !== null &&
    viewerDiffersFromEstablishment(schedule.timezone, viewerTimezone);
  if (!showViewer) {
    return day.windows.map(window => `${window.start}–${window.end}`).join(', ');
  }
  return day.windows
    .map(window =>
      formatWindowWithViewerLocal(window, day.day, schedule, viewerTimezone)
    )
    .join(', ');
}
